import { AppIcon } from '@/components/ui/app-icon';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOpenUpgrade } from '@/hooks/use-open-upgrade.hook';
import { useOrganizationActionFence } from '@/hooks/use-organization-action-fence.hook';
import { usePageRestored } from '@/hooks/use-page-restored.hook';
import { TrialStartNowDialog } from '@/components/trial/trial-start-now-dialog.component';
import { extraSeatNote } from '@/lib/card-trial';
import { formatCredits, getPlanCredits } from '@/lib/credits';
import { selectSuggestedUpgradePlans } from '@/lib/plan-upgrade-utils';
import { cn } from '@/lib/utils';
import { CREDIT_PACKS, CREDIT_PACK_PRICE } from '@/lib/credit-packs';
import { redirectToStripeCheckout } from '@/lib/stripe-redirect';
import {
  canOfferCreditTopups,
  canOfferStripePurchases,
} from '@/lib/billing-purchase-access';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  useCreateCheckoutSessionMutation,
  useCreateCreditTopupMutation,
  useGetPlansQuery,
  usePreviewPlanChangeMutation,
  type CreateCheckoutSessionRequest,
} from '@/store/features/billing.feature';
import { useGetOrganizationCreditsQuery } from '@/store/features/extension.feature';
import { closeCreditGate, selectCreditGate } from '@/store/features/ui.feature';
import { plural } from '@/containers/upgrade/upgrade-model';
import {
  checkoutRequest,
  isCharge,
  previewRequest,
  startNowOrder,
  type UpgradeOrder,
} from '@/containers/upgrade/upgrade-order';
import { toUpgradeTier } from '@/lib/upgrade-link';
import type { Plan } from '@/types';
import { iconCoins, iconSpinnerThird } from '@/lib/icons';
import { brandPlanName } from '@/lib/brand';

/**
 * Global insufficient-credits dialog — the upgrade path that replaces the
 * dead-end "Insufficient credits. Please purchase more credits." toast.
 *
 * Mounted once in AppShell; opened from any call site via
 * `useCreditGate().openCreditGate({ needed, action })` when an action 402s.
 * Shows needed-vs-available up top, the suggested plan upgrades, and a
 * one-time credit-pack dropdown with instant Stripe checkout.
 *
 * AppSumo fencing (fail closed): AppSumo-only orgs have no Stripe
 * subscription — the topup/checkout mutations would fail — so they get a
 * link to /plans instead of purchase paths. While plans/feature-access are
 * loading or errored we also show no purchase paths.
 */

/**
 * "Includes 3,500 extra credits and 2 extra senders": what a v4 order adds
 * to its seats; null without add-ons.
 */
function addonNote(order: UpgradeOrder): string | null {
  const parts = [
    order.addonCredits && `${formatCredits(order.addonCredits)} extra credits`,
    order.senders && plural(order.senders, 'extra sender'),
  ].filter((part): part is string => !!part);
  return parts.length ? `Includes ${parts.join(' and ')}` : null;
}

/**
 * The plan a card trial would start today, awaiting the charge confirmation.
 * On v4, `order` is what starting it sends, the trial's renewal on that plan,
 * and `chargeNow` is billing's preview of it; earlier pricing has neither.
 */
interface StartNow {
  plan: Plan;
  order: UpgradeOrder | null;
  chargeNow?: number;
}

export function CreditGateDialog() {
  const dispatch = useAppDispatch();
  const gate = useAppSelector(selectCreditGate);

  const {
    isAppSumoOnly,
    isReady,
    error: featureAccessError,
    trial,
    organizationId,
  } = useFeatureAccess();
  const { beginOrganizationAction, isOrganizationActionCurrent } =
    useOrganizationActionFence(organizationId);
  const { currentData: creditsData } = useGetOrganizationCreditsQuery(
    { organizationId },
    { skip: !gate?.open || !organizationId }
  );
  const {
    currentData: plans,
    isLoading: plansLoading,
    isError: plansError,
  } = useGetPlansQuery(
    { organizationId },
    { skip: !gate?.open || !organizationId }
  );

  // Free Trial v1: during the trial the credit pool IS the meter — no
  // top-up packs (the BE rejects them too); the plan upgrades are the path.
  const isTrialActive = trial?.active === true;
  // Card-backed trial: "Upgrade" starts the plan today so its credits land now.
  const isCardTrial = isTrialActive && Boolean(trial?.renews_to);

  const [packSize, setPackSize] = useState<number>(CREDIT_PACKS[0]);
  const [isToppingUp, setIsToppingUp] = useState(false);
  const [processingPriceId, setProcessingPriceId] = useState<string | null>(
    null
  );
  // Browser Back from Checkout: the page returns with the spinner still on.
  usePageRestored(() => {
    setIsToppingUp(false);
    setProcessingPriceId(null);
  });
  const [startNow, setStartNow] = useState<StartNow | null>(null);

  const [createCreditTopup] = useCreateCreditTopupMutation();
  const [createCheckoutSession] = useCreateCheckoutSessionMutation();
  const [previewPlanChange] = usePreviewPlanChangeMutation();

  // A pending order belongs to the org it was quoted for: switching orgs
  // (here or in another tab) drops it, so its confirm can't reach another
  // org's subscription.
  useEffect(() => {
    setIsToppingUp(false);
    setProcessingPriceId(null);
    setStartNow(null);
  }, [organizationId]);

  // The balance as the sidebar shows it, to the tenth.
  const available = creditsData ? formatCredits(creditsData.count) : null;
  const needed = gate?.needed;

  const suggestedPlans = selectSuggestedUpgradePlans({
    plans: plans?.plans,
    currentPlanId: plans?.current_plan?.id,
  });

  // Fail closed: only offer Stripe purchase paths once feature-access AND
  // plans have resolved and the org is not AppSumo-only.
  const purchaseReadiness = {
    organizationId,
    featureAccessReady: isReady,
    featureAccessError: Boolean(featureAccessError),
    plansReady: Boolean(plans),
    plansError,
    isAppSumoOnly,
    trialActive: trial?.active,
  };
  const showStripePaths = canOfferStripePurchases(purchaseReadiness);
  const showTopups = canOfferCreditTopups(purchaseReadiness);
  const billingChecksResolved =
    Boolean(organizationId) &&
    isReady &&
    !featureAccessError &&
    Boolean(plans) &&
    !plansError &&
    !plansLoading;

  const close = () => dispatch(closeCreditGate());
  const openUpgrade = useOpenUpgrade();

  const handleTopup = async () => {
    const action = beginOrganizationAction();
    if (!action || isToppingUp) return;
    setIsToppingUp(true);
    try {
      const response = await createCreditTopup({
        organizationId: action.organizationId,
        credits: packSize,
      }).unwrap();
      if (!isOrganizationActionCurrent(action)) return;
      const redirected = await redirectToStripeCheckout(response, () =>
        isOrganizationActionCurrent(action)
      );
      if (!isOrganizationActionCurrent(action)) return;
      if (!redirected) setIsToppingUp(false);
    } catch (err) {
      if (!isOrganizationActionCurrent(action)) return;
      toast.error(
        (err as { data?: { detail?: string } })?.data?.detail ||
          'Could not start the top-up checkout.'
      );
      setIsToppingUp(false);
    }
  };

  // The team's seats, never fewer than a plan includes: the checkout preflight
  // rejects a plan that can't hold the current members (a trial has 3 seats).
  const seatsFor = (plan: Plan) =>
    Math.max(plan.base_seats ?? 1, plans?.seats_used ?? 1);

  const handleUpgrade = async (
    request: Omit<CreateCheckoutSessionRequest, 'organizationId'>
  ) => {
    const action = beginOrganizationAction();
    if (!action || processingPriceId) return;
    setProcessingPriceId(request.price_id);
    try {
      const response = await createCheckoutSession({
        organizationId: action.organizationId,
        ...request,
      }).unwrap();
      if (!isOrganizationActionCurrent(action)) return;
      if (response.action === 'modified') {
        window.location.href = response.url || '/plans?status=success';
        return;
      }
      const redirected = await redirectToStripeCheckout(response, () =>
        isOrganizationActionCurrent(action)
      );
      if (!isOrganizationActionCurrent(action)) return;
      if (!redirected) setProcessingPriceId(null);
    } catch (err) {
      if (!isOrganizationActionCurrent(action)) return;
      toast.error(
        (err as { data?: { detail?: string } })?.data?.detail ||
          (err as Error)?.message ||
          'Could not start the checkout.'
      );
      setProcessingPriceId(null);
    }
  };

  /**
   * A card-backed trial's "Start today": it charges the saved card with no
   * Stripe page, so the charge is confirmed first, as Plans does. On v4 the
   * order is the trial's renewal on the chosen plan (its interval, seats and
   * add-ons), priced by billing's preview of exactly that order.
   */
  const startToday = async (plan: Plan, priceId: string) => {
    if (plans?.pricing_version !== 'v4') {
      setStartNow({ plan, order: null });
      return;
    }
    const tier = toUpgradeTier(plan.tier_key ?? undefined);
    const order = tier ? startNowOrder(plans, trial, tier) : null;
    const action = beginOrganizationAction();
    if (!action || processingPriceId) return;
    if (!order) {
      toast.error('Could not confirm the charge. Please try again.');
      return;
    }
    setProcessingPriceId(priceId);
    try {
      const preview = await previewPlanChange({
        organizationId: action.organizationId,
        ...previewRequest(order),
      }).unwrap();
      if (!isOrganizationActionCurrent(action)) return;
      if (!isCharge(preview.proration_amount)) {
        throw new Error('Charge preview unavailable');
      }
      setStartNow({ plan, order, chargeNow: preview.proration_amount });
    } catch {
      if (isOrganizationActionCurrent(action)) {
        toast.error('Could not confirm the charge. Please try again.');
      }
    } finally {
      if (isOrganizationActionCurrent(action)) setProcessingPriceId(null);
    }
  };

  return (
    <Dialog open={!!gate?.open} onOpenChange={(open) => !open && close()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AppIcon
              icon={iconCoins}
              className='text-amber-500'
              style={{ fontSize: 16 }}
            />
            Not enough credits
          </DialogTitle>
          <DialogDescription>
            {typeof needed === 'number' && needed > 0 ? (
              <>
                This action needs{' '}
                <span className='font-semibold text-foreground'>
                  {needed.toLocaleString()} credit{needed === 1 ? '' : 's'}
                </span>
                {available != null && (
                  <>
                    {' '}
                    — you have{' '}
                    <span className='font-semibold text-foreground'>
                      {available}
                    </span>
                  </>
                )}
                .
              </>
            ) : available != null ? (
              <>
                You have{' '}
                <span className='font-semibold text-foreground'>
                  {available} credit
                  {available === '1' ? '' : 's'}
                </span>{' '}
                left — not enough for this action.
              </>
            ) : (
              <>Your balance is too low for this action.</>
            )}{' '}
            {isCardTrial
              ? "Start a plan today to unlock its credits now. It's charged to the card you added, and your trial ends."
              : isTrialActive
                ? "You've used your trial credits — pick a plan to keep enriching."
                : 'Add credits to keep going.'}
          </DialogDescription>
        </DialogHeader>

        {showStripePaths ? (
          <div className='space-y-4'>
            {/* Plan upgrades — recurring credits, the better deal */}
            {suggestedPlans.length > 0 && (
              <div className='space-y-2'>
                <p className='text-[12px] font-medium text-muted-foreground'>
                  {isCardTrial ? 'Start a plan today' : 'Upgrade your plan'}
                </p>
                {suggestedPlans.map((plan) => {
                  const priceId = plan.monthly_price?.id;
                  const monthlyPrice = plan.monthly_price?.price || 0;
                  const planCredits = getPlanCredits(plan);
                  return (
                    <div
                      key={plan.id}
                      className='flex items-center justify-between rounded-xl border border-border p-3'
                    >
                      <div>
                        <p className='text-sm font-semibold text-foreground'>
                          {brandPlanName(plan.name)}
                        </p>
                        {planCredits > 0 && (
                          <p className='flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400'>
                            <AppIcon
                              icon={iconCoins}
                              className='text-emerald-500'
                              style={{ fontSize: 11 }}
                            />
                            {planCredits.toLocaleString()} credits/mo
                          </p>
                        )}
                      </div>
                      <div className='flex items-center gap-3'>
                        <span className='text-sm font-bold text-foreground'>
                          ${monthlyPrice.toFixed(0)}
                          <span className='text-[10px] font-normal text-muted-foreground'>
                            /mo
                          </span>
                        </span>
                        <Button
                          size='sm'
                          className='h-8 text-xs'
                          disabled={!priceId || processingPriceId === priceId}
                          aria-label={
                            isCardTrial
                              ? `Start ${brandPlanName(plan.name)} today`
                              : `Upgrade to ${brandPlanName(plan.name)}`
                          }
                          onClick={() => {
                            if (!priceId) return;
                            if (isCardTrial) {
                              void startToday(plan, priceId);
                              return;
                            }
                            // A plan change goes through the wizard, which
                            // previews it; one click here charged at once.
                            close();
                            openUpgrade({
                              plan: toUpgradeTier(plan.tier_key ?? undefined),
                              source: 'credit_gate',
                            });
                          }}
                        >
                          {processingPriceId === priceId ? (
                            <AppIcon
                              icon={iconSpinnerThird}
                              className='motion-safe:animate-spin'
                              style={{ fontSize: 12 }}
                              aria-hidden='true'
                            />
                          ) : isCardTrial ? (
                            'Start today'
                          ) : (
                            'Upgrade'
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* One-time credit packs — instant checkout. Hidden during a
                trial (BE rejects trial top-ups; plans are the path). */}
            {showTopups && (
              <div className='space-y-2'>
                <p className='text-[12px] font-medium text-muted-foreground'>
                  Or buy one-time credits
                </p>
                <div className='flex items-center gap-2'>
                  <Select
                    value={String(packSize)}
                    onValueChange={(v) => setPackSize(Number(v))}
                  >
                    <SelectTrigger
                      className='h-9 flex-1'
                      aria-label='One-time credit pack'
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CREDIT_PACKS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n.toLocaleString()} credits · $
                          {(n * CREDIT_PACK_PRICE).toFixed(0)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    className={cn('h-9 shrink-0')}
                    disabled={isToppingUp}
                    onClick={handleTopup}
                    aria-label={`Buy ${packSize.toLocaleString()} one-time credits`}
                  >
                    {isToppingUp && (
                      <AppIcon
                        icon={iconSpinnerThird}
                        className='mr-2 motion-safe:animate-spin'
                        style={{ fontSize: 14 }}
                        aria-hidden='true'
                      />
                    )}
                    {isToppingUp ? 'Opening checkout...' : 'Buy now'}
                  </Button>
                </div>
                <p className='text-[11px] text-muted-foreground'>
                  ${CREDIT_PACK_PRICE.toFixed(2)} per credit · one-time, never
                  expires.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className='rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground'>
            {billingChecksResolved && isAppSumoOnly ? (
              <>
                Your AppSumo plan refills monthly. To add more credits, manage
                your plan on the{' '}
                <a href='/plans' className='font-medium text-primary underline'>
                  plans page
                </a>
                .
              </>
            ) : (
              <>
                Loading your plan options… If this persists, visit the{' '}
                <a href='/plans' className='font-medium text-primary underline'>
                  plans page
                </a>
                .
              </>
            )}
          </div>
        )}
      </DialogContent>
      {startNow ? (
        <StartNowConfirm
          startNow={startNow}
          seats={seatsFor(startNow.plan)}
          extraSeatCents={plans?.extra_seat?.monthly_price_cents}
          busy={processingPriceId !== null}
          onClose={() => setStartNow(null)}
          onConfirm={(request) => void handleUpgrade(request)}
        />
      ) : null}
    </Dialog>
  );
}

/**
 * The charge confirmation for starting a card trial today. A v4 order shows
 * billing's preview of the charge, its seats and add-ons, the credits it
 * unlocks and what it renews at; earlier pricing shows the plan's monthly
 * price for the team's seats, as before.
 */
function StartNowConfirm({
  startNow: { plan, order, chargeNow },
  seats,
  extraSeatCents,
  busy,
  onClose,
  onConfirm,
}: {
  startNow: StartNow;
  /** The team's seats, for an earlier-pricing plan. */
  seats: number;
  extraSeatCents?: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: (
    request: Omit<CreateCheckoutSessionRequest, 'organizationId'>
  ) => void;
}) {
  const priceId = plan.monthly_price?.id;
  return (
    <TrialStartNowDialog
      open
      onOpenChange={(open) => !open && onClose()}
      planLabel={brandPlanName(plan.name)}
      tier={order ? `v4:${order.tier}` : (plan.tier_key ?? '')}
      price={order ? (order.total ?? undefined) : plan.monthly_price?.price}
      chargeNow={chargeNow}
      interval={order?.interval ?? 'month'}
      credits={order ? order.credits : getPlanCredits(plan)}
      extraSeats={
        order
          ? plural(order.seats, 'seat')
          : extraSeatNote(seats, plan.base_seats ?? 1, extraSeatCents)
      }
      addons={order ? (addonNote(order) ?? undefined) : undefined}
      busy={busy}
      onConfirm={() => {
        if (order) onConfirm(checkoutRequest(order, 'start_now'));
        else if (priceId)
          onConfirm({ price_id: priceId, start_now: true, seats });
      }}
    />
  );
}
